<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AiBackfillAPI from 'dashboard/api/aiBackfill';

// Botón junto a "Gestión Plus"/"Abrir en CRM" que reconcilia la conversación
// con el histórico REAL de Twilio: recupera los mensajes SALIENTES que la IA
// (n8n) envió directamente por Twilio y crea los que falten como notas
// privadas con su fecha de envío original, anotando errores de entrega.
// Disponible SOLO para administradores (por ahora).
const props = defineProps({
  conversation: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();

const isTwilioConversation = computed(() => {
  const channel = props.conversation?.meta?.channel || '';
  return channel === 'Channel::TwilioSms';
});

// Acceso configurable por rol en Ajustes > Features (ai_backfill_enabled +
// ai_backfill_roles). Los administradores siempre tienen acceso.
const hasAccess = computed(() => {
  if (store.getters.getCurrentRole === 'administrator') return true;
  const accountId = store.getters.getCurrentAccountId;
  const account = store.getters['accounts/getAccount']?.(accountId) || {};
  const settings = account.settings || {};
  const enabled =
    settings.ai_backfill_enabled === true ||
    settings.ai_backfill_enabled === 'true';
  if (!enabled) return false;
  const allowed = Array.isArray(settings.ai_backfill_roles)
    ? settings.ai_backfill_roles.map(r => String(r))
    : [];
  if (!allowed.length) return false;
  const userRoles = [
    String(store.getters.getCurrentRole || ''),
    String(store.getters.getCurrentCustomRoleId || ''),
  ];
  return userRoles.some(r => allowed.includes(r));
});

const isVisible = computed(
  () => hasAccess.value && isTwilioConversation.value && props.conversation?.id
);

const isBusy = ref(false);

const onClick = async () => {
  if (isBusy.value) return;
  isBusy.value = true;
  // eslint-disable-next-line no-console
  console.log('[ai-backfill] reconciling conversation', props.conversation.id);
  try {
    const { data } = await AiBackfillAPI.run(props.conversation.id);
    // eslint-disable-next-line no-console
    console.log('[ai-backfill] result', data);
    useAlert(
      t('AI_BACKFILL.SUCCESS', {
        added: data.added,
        linked: data.linked,
        skipped: data.skipped,
        errors: data.errors_annotated,
      })
    );
    if (data.added > 0 || data.linked > 0) {
      // Reordenar los mensajes del store: las notas backdatadas llegan por
      // WebSocket (vía Sidekiq) al final de la lista; esperamos un instante a
      // que aterricen y esta acción las intercala por fecha.
      await new Promise(resolve => {
        setTimeout(resolve, 1500);
      });
      await store.dispatch('syncActiveConversationMessages', {
        conversationId: props.conversation.id,
      });
    }
  } catch (e) {
    const msg = e?.response?.data?.error || e?.message || 'error';
    // eslint-disable-next-line no-console
    console.error('[ai-backfill] failed:', msg);
    useAlert(t('AI_BACKFILL.ERROR', { error: msg }));
  } finally {
    isBusy.value = false;
  }
};
</script>

<template>
  <button
    v-if="isVisible"
    type="button"
    class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-background px-3 py-1.5 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    :disabled="isBusy"
    :title="t('AI_BACKFILL.TOOLTIP')"
    @click="onClick"
  >
    <span
      class="size-3.5"
      :class="isBusy ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-bot'"
    />
    <span class="hidden md:inline">{{ t('AI_BACKFILL.BUTTON') }}</span>
  </button>
</template>
